/* eslint-disable no-alert */
import React, { useCallback, useEffect, useRef, createRef, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { AssetContentItem } from '@components/PracticeV2/Component/QuestionAssets/QuestionAssets';
import { TabProvider } from '@components/Shared/tabs';
import { useUser } from '@reducers/user/hooks';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import { TestWrapper } from '@learnosityassessment/Components/TestHarness/styles';
import '@learnosityassessment/styles.css';
import { TestHarness } from '@learnosityassessment/Components/TestHarness';
import { useFeatureFlagEnabled } from '@components/Atoms/FeatureFlagGuard/hooks';
import ShowIf from '@components/Atoms/ShowIf';
import { fetchContent } from '../../apis/practice';
import {
  PrimaryWrap,
  DefaultContentWrap,
  BlockWrap,
  BlockTitle,
} from '../Generals/stylesheets/General.style';
import AnswerGridChoice from '../AnswerGridChoice';
import AnswerMultiChoice from '../AnswerMultiChoice';
import ShadowScrollbar from '../Scrollbar/ShadowScrollbars';
import { QUESTION_TYPES } from '../../utils/enums';
import {
  QuestionAnswer,
  QuestionDesc,
  QuestionContentWrap,
} from '../QuestionItem/QuestionItem.style';
import MathJaxItem from '../MathJaxContainer';
import { AnswerExplanationDecs } from '../AnswerExplanation/AnswerExplanation.style';
import {
  MainSelectorWrapper,
  AssetViewerWrapper,
  QuestionViewerWrapper,
  ItemViewerWrapper,
} from './QuestionReviewer.style';

const scrollStyle = {
  height: 'calc(110vh - 20em)',
  width: '100%',
};

const CONTENT_TYPE = {
  QUESTION: 'question',
  ASSET: 'asset',
  LRN_ITEM: 'lrn_item',
  LRN_ACTIVITY: 'lrn_activity',
};

const QuestionReviewer = () => {
  const currentUser = useUser();
  const isQuestionReviewerEnabled = useFeatureFlagEnabled({
    featureName: 'question-reviewer',
    suspense: true,
  });

  const searchParams = useSearchParams();
  const hasAutoFetchedRef = useRef(false);
  const [questionId, setQuestionId] = useState(searchParams?.question_id || '');
  const [assetId, setAssetId] = useState(searchParams?.asset_id || '');
  const [itemId, setItemId] = useState(searchParams?.item_id || '');
  const [activityId, setActivityId] = useState(searchParams?.activity_id || '');
  const [data, setData] = useState(null);
  const [questionAssets, setQuestionAssets] = useState([]);
  const [questionAssetsLoading, setQuestionAssetsLoading] = useState(false);
  const widgetRef = createRef();
  const [loading, setLoading] = useState(false);
  const [previewType, setPreviewType] = useState(() => {
    if (searchParams?.question_id) {
      return CONTENT_TYPE.QUESTION;
    }
    if (searchParams?.asset_id) {
      return CONTENT_TYPE.ASSET;
    }
    if (searchParams?.item_id) {
      return CONTENT_TYPE.LRN_ITEM;
    }
    if (searchParams?.activity_id) {
      return CONTENT_TYPE.LRN_ACTIVITY;
    }
    return null;
  });

  // redirect to /dashboard if not chalktalk internal user
  useEffect(() => {
    if (
      !isQuestionReviewerEnabled ||
      (currentUser &&
        (!currentUser?.email.includes('chalktalk') || currentUser?.role === 'Student'))
    ) {
      window.location.href = '/dashboard';
    }
  }, [currentUser, isQuestionReviewerEnabled]);

  useEffect(() => {
    const trimmedItemId = itemId?.trim();
    const trimmedActivityId = activityId?.trim();

    if (trimmedActivityId) {
      if (previewType !== CONTENT_TYPE.LRN_ACTIVITY) {
        setPreviewType(CONTENT_TYPE.LRN_ACTIVITY);
      }
      setData(null);
      return;
    }

    if (trimmedItemId) {
      if (previewType !== CONTENT_TYPE.LRN_ITEM) {
        setPreviewType(CONTENT_TYPE.LRN_ITEM);
      }
      setData(null);
      return;
    }

    if ([CONTENT_TYPE.LRN_ITEM, CONTENT_TYPE.LRN_ACTIVITY].includes(previewType)) {
      setPreviewType(null);
      setData(null);
    }
  }, [activityId, itemId, previewType]);

  const getInternalData = useCallback(async (type, identifier) => {
    setData(null);
    setPreviewType(null);
    const id = parseInt(identifier.trim(), 10);
    if (!id || Number.isNaN(id)) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetchContent(type, id);
      if ('error' in response) {
        alert(response.error);
        setLoading(false);
        return;
      }
      setData(response?.response?.data || {});
      setPreviewType(type);
    } catch (err) {
      alert(err?.message || 'An error occurred while fetching content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAutoFetchedRef.current) {
      return;
    }

    const initialQuestionId = searchParams?.question_id?.trim();
    const initialAssetId = searchParams?.asset_id?.trim();
    const initialItemId = searchParams?.item_id?.trim();
    const initialActivityId = searchParams?.activity_id?.trim();
    if (initialQuestionId) {
      hasAutoFetchedRef.current = true;
      setQuestionId(initialQuestionId);
      getInternalData(CONTENT_TYPE.QUESTION, initialQuestionId);
      return;
    }

    if (initialAssetId) {
      hasAutoFetchedRef.current = true;
      setAssetId(initialAssetId);
      getInternalData(CONTENT_TYPE.ASSET, initialAssetId);
      return;
    }

    if (initialActivityId) {
      hasAutoFetchedRef.current = true;
      setActivityId(initialActivityId);
      setItemId('');
      setData(null);
      setPreviewType(CONTENT_TYPE.LRN_ACTIVITY);
      return;
    }

    if (initialItemId) {
      hasAutoFetchedRef.current = true;
      setItemId(initialItemId);
      setActivityId('');
      setData(null);
      setPreviewType(CONTENT_TYPE.LRN_ITEM);
    }
  }, [getInternalData, searchParams]);

  useEffect(() => {
    let isMounted = true;
    const loadQuestionAssets = async () => {
      const sharedAssets = data?.shared_assets;
      if (
        previewType !== CONTENT_TYPE.QUESTION ||
        !Array.isArray(sharedAssets) ||
        sharedAssets.length === 0
      ) {
        if (isMounted) {
          setQuestionAssets([]);
          setQuestionAssetsLoading(false);
        }
        return;
      }

      // FullQuestionSerializer already returns full asset payloads; support legacy ID lists too.
      const hasInlineAssetObjects = sharedAssets.every((asset) => asset && typeof asset === 'object');
      if (hasInlineAssetObjects) {
        if (isMounted) {
          setQuestionAssets(sharedAssets.filter(Boolean));
          setQuestionAssetsLoading(false);
        }
        return;
      }

      setQuestionAssetsLoading(true);
      const assets = await Promise.all(
        sharedAssets.map(async (sharedAsset) => {
          const sharedAssetId =
            sharedAsset && typeof sharedAsset === 'object' ? sharedAsset.id : sharedAsset;
          const response = await fetchContent(CONTENT_TYPE.ASSET, sharedAssetId);
          if ('error' in response) {
            return null;
          }
          return response?.response?.data || null;
        }),
      );

      if (!isMounted) {
        return;
      }

      setQuestionAssets(assets.filter(Boolean));
      setQuestionAssetsLoading(false);
    };

    loadQuestionAssets();

    return () => {
      isMounted = false;
    };
  }, [data?.shared_assets, previewType]);

  const renderAnswer = (questionData) => {
    if (_isEmpty(questionData)) {
      return null;
    }
    const answerType = questionData?.question_format;
    const choices = questionData?.choices;
    const correctChoices = questionData?.correct_choices;

    switch (answerType) {
      case QUESTION_TYPES.GRID_IN: {
        return (
          <AnswerGridChoice
            selectedValue={[]}
            correctAnswer={correctChoices}
            score={0}
            shouldDisplayAnswer
            chooseAnswer={() => {
              // Read-only view, no action needed
            }}
          />
        );
      }
      case QUESTION_TYPES.MULTIPLE_CHOICES:
      default:
        return (
          <AnswerMultiChoice
            chooseAnswer={() => {
              // Read-only view, no action needed
            }}
            choices={choices}
            correctChoice={correctChoices}
            userChoice={[]}
            shouldDisplayAnswer
          />
        );
    }
  };

  const isAllowedQuestionReviewerUser =
    !!currentUser &&
    currentUser?.email?.includes('chalktalk') &&
    currentUser?.role !== 'Student';

  if (!isQuestionReviewerEnabled || !isAllowedQuestionReviewerUser) {
    return null;
  }

  return (
    <PrimaryWrap>
      <DefaultContentWrap>
        <MainSelectorWrapper>
          <BlockWrap>
            <BlockTitle>Legacy Question Viewer</BlockTitle>
            <p>Enter a question id to review its assets</p>
            <p>
              <input
                type="text"
                value={questionId}
                placeholder="question_id"
                onChange={(e) => setQuestionId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    getInternalData(CONTENT_TYPE.QUESTION, questionId);
                  }
                }}
              />
              &nbsp;
              <button
                type="button"
                onClick={() => getInternalData(CONTENT_TYPE.QUESTION, questionId)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Fetch'}
              </button>
            </p>
          </BlockWrap>
          <BlockWrap>
            <BlockTitle>Legacy Asset Viewer</BlockTitle>
            <p>Enter an asset id to review it alone</p>
            <p>
              <input
                type="text"
                value={assetId}
                placeholder="asset_id"
                onChange={(e) => setAssetId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    getInternalData(CONTENT_TYPE.ASSET, assetId);
                  }
                }}
              />
              &nbsp;
              <button
                type="button"
                onClick={() => getInternalData(CONTENT_TYPE.ASSET, assetId)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Fetch'}
              </button>
            </p>
          </BlockWrap>
          <BlockWrap>
            <BlockTitle>Learnosity Item Viewer</BlockTitle>
            <p>Enter an item reference id to preview it</p>
            <p>
              <input
                type="text"
                value={itemId}
                placeholder="item reference id"
                onChange={(e) => setItemId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    window.location.href = `/debug/questions/?item_id=${itemId.trim()}`;
                  }
                }}
              />
              &nbsp;
              <button
                type="button"
                onClick={() => {
                  window.location.href = `/debug/questions/?item_id=${itemId.trim()}`;
                }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Fetch Item'}
              </button>
            </p>
          </BlockWrap>
          <BlockWrap>
            <BlockTitle>Learnosity Activity Viewer</BlockTitle>
            <p>Enter an activity template id to preview it</p>
            <p>
              <input
                type="text"
                value={activityId}
                onChange={(e) => setActivityId(e.target.value)}
                placeholder="activity template id"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    window.location.href = `/debug/questions/?activity_id=${activityId.trim()}`;
                  }
                }}
              />
              &nbsp;
              <button
                type="button"
                onClick={() => {
                  window.location.href = `/debug/questions/?activity_id=${activityId.trim()}`;
                }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Fetch Activity'}
              </button>
            </p>
          </BlockWrap>
        </MainSelectorWrapper>
        <ShowIf If={previewType === CONTENT_TYPE.QUESTION && !!data}>
          <QuestionViewerWrapper>
            <ShadowScrollbar autoHide style={scrollStyle}>
              <BlockWrap>
                <BlockTitle>Question Prompt ({questionId || 'N/A'})</BlockTitle>
                <QuestionDesc>
                  <MathJaxItem data={data?.prompt?.text} />
                </QuestionDesc>
              </BlockWrap>
              <BlockWrap>
                <BlockTitle>
                  Question Choices ([
                  {data?.choices?.map((choice) => choice.id)?.join(',') || ''}])
                </BlockTitle>
                <QuestionAnswer>{renderAnswer(data)}</QuestionAnswer>
              </BlockWrap>
              <ShowIf If={questionAssetsLoading || questionAssets.length > 0}>
                <BlockWrap>
                  <BlockTitle>Associated Shared Assets</BlockTitle>
                  <ShowIf If={questionAssetsLoading}>
                    <p>Loading shared assets...</p>
                  </ShowIf>
                  <ShowIf If={questionAssets.length > 0}>
                    {questionAssets.map((asset) => (
                      <div key={asset.id} style={{ marginBottom: '1rem' }}>
                        <p>
                          <a
                            href={`/debug/questions/?asset_id=${asset.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open asset {asset.id} in standalone preview
                          </a>
                        </p>
                        <TabProvider defaultTab={asset.id}>
                          <AssetContentItem asset={asset} />
                        </TabProvider>
                      </div>
                    ))}
                  </ShowIf>
                </BlockWrap>
              </ShowIf>
            </ShadowScrollbar>
            <ShadowScrollbar autoHide style={scrollStyle}>
              <BlockWrap>
                <BlockTitle>Answer Explanation</BlockTitle>
                <QuestionContentWrap>
                  <AnswerExplanationDecs>
                    <MathJaxItem data={data?.explanation?.text} />
                  </AnswerExplanationDecs>
                </QuestionContentWrap>
              </BlockWrap>
            </ShadowScrollbar>
          </QuestionViewerWrapper>
        </ShowIf>
        <ShowIf If={previewType === CONTENT_TYPE.ASSET && !!data}>
          <AssetViewerWrapper>
            <BlockWrap>
              <BlockTitle>Single Asset Render</BlockTitle>
              <TabProvider defaultTab={data?.id}>
                <AssetContentItem asset={data} />
              </TabProvider>
            </BlockWrap>
          </AssetViewerWrapper>
        </ShowIf>
        <ShowIf
          If={[CONTENT_TYPE.LRN_ITEM, CONTENT_TYPE.LRN_ACTIVITY].includes(previewType)}
        >
          <ItemViewerWrapper>
            <TestWrapper>
              <TestHarness
                ref={widgetRef}
                name="ChalkTalk Preview Activity"
                userId="1"
                sessionId="00000000-0000-4000-8000-000000000000"
                activityId="chalktalk_reviewer_activity"
                activityTemplateId={!activityId || activityId === '' ? null : activityId}
                questionReferences={
                  !itemId || itemId === ''
                    ? undefined
                    : [{ reference: itemId, id: itemId }]
                }
                renderingType="assess"
                type="local_practice"
                state="initial"
              />
            </TestWrapper>
          </ItemViewerWrapper>
        </ShowIf>
      </DefaultContentWrap>
    </PrimaryWrap>
  );
};

export default QuestionReviewer;
